/**
 * Close the mobile navigation drawer by dragging it toward its edge.
 *
 * The drawer already closes by tapping the backdrop, the X, or the "back"
 * button. What was missing was what a thumb expects from a panel that
 * entered by sliding in: being able to send it back the way it came. The
 * gesture is symmetric to the entrance animation, so the drag *is* the same
 * transformation, only driven by the finger instead of the clock.
 *
 * Everything that has physics —8px dead zone, axis lock, fling, rubber-band,
 * haptic on crossing the threshold— comes from `lib/gestures`: none of
 * those four things is reimplemented here, only what they mean for a
 * drawer anchored to the left is decided.
 *
 * Two deliberate asymmetries:
 *
 *   - **Only left counts.** There's nothing to open toward the right, so
 *     that drag gets a short, bounded rubber-band (it acknowledges the
 *     finger) and NEVER commits: `direction === "left"` is checked, not just
 *     `committed`. Without that check a quick flick to the right would close
 *     the drawer, which is exactly the opposite of what the gesture asks for.
 *   - **The axis is just "x".** A vertical drag aborts the gesture instead of
 *     competing with it, so the drawer's navigation keeps scrolling normally.
 *     The container's `touch-action: pan-y` is the other half of that deal:
 *     the browser keeps Y and hands us X.
 *
 * `prefers-reduced-motion` is deliberately not consulted here: the drag isn't
 * animation, it's direct response to the finger. What IS animation —the
 * final settle on release— is collapsed by `global.css`'s global guard.
 */

import { createSignal, getOwner, onCleanup, runWithOwner } from "solid-js";

import { isEditableTarget } from "@/lib/gestures/guards";
import { NAV_DRAWER_DISMISS } from "@/lib/gestures/presets";
import { applyRubberBand, computeCommit, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { attachTouchMouse } from "@/lib/gestures/solid";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  rubberBandLimitPx: RUBBER_BAND_LIMIT_PX,
  hapticMs: HAPTIC_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  fling: DRAWER_FLING,
} = NAV_DRAWER_DISMISS;

interface NavDrawerSwipeOptions {
  /** The drawer is open (with the drawer closed the gesture doesn't even start). */
  isOpen: () => boolean;
  /** Close. Called exactly once, when the drag is confirmed. */
  close: () => void;
  /** Drawer panel; its measured width calibrates the backdrop fade. */
  panel?: () => HTMLElement | undefined;
}

export interface NavDrawerSwipe {
  /** Current panel offset in px (≤ 0 except for rubber-band). */
  dragPx: () => number;
  /** A drag is in progress: the CSS transition must be off. */
  isDragging: () => boolean;
  /** Backdrop opacity while dragging (1 at rest). */
  backdropOpacity: () => number;
  /** `ref` of the element that listens for the gesture (the drawer box). */
  attach: (element: HTMLElement) => void;
}

/** Panel offset for a finger delta. */
function panelOffsetFor(dx: number): number {
  if (dx <= 0) {
    // Toward the edge: 1:1 with the finger. Any smoothing here shows up as
    // lag, because the panel is exactly what the finger thinks it's touching.
    return dx;
  }
  // Toward the inside: rubber-band (dx * factor) plus a hard cap, so the
  // panel doesn't pull away from its edge no matter how much you push.
  return Math.min(applyRubberBand(dx, 0, RUBBER_BAND_FACTOR), RUBBER_BAND_LIMIT_PX);
}

export function useNavDrawerSwipe(options: NavDrawerSwipeOptions): NavDrawerSwipe {
  const [dragPx, setDragPx] = createSignal(0);
  const [isDragging, setIsDragging] = createSignal(false);
  // Measured at the start of each gesture: the width changes across screen
  // rotations. The type is explicit because the preset is `as const` and the
  // initial value would pin the literal type `280`.
  let widthPx: number = FALLBACK_WIDTH_PX;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: DRAWER_FLING,
    haptics: true,
    hapticMs: HAPTIC_MS,
    commitPx: COMMIT_THRESHOLD_PX,
    canStart: ({ target }) => {
      if (!options.isOpen()) {
        return false;
      }
      // A text field inside the drawer keeps the drag for itself: there,
      // sliding means selecting text or moving the cursor, not closing.
      return !isEditableTarget(target);
    },
    onStart: () => {
      // `offsetWidth` is 0 on an unmeasured or hidden panel: hence the
      // explicit `> 0` and not a `??` — zero is a missing measurement, not a width.
      const medido = options.panel?.()?.offsetWidth;
      widthPx = medido !== undefined && medido > 0 ? medido : FALLBACK_WIDTH_PX;
      setDragPx(0);
      setIsDragging(true);
    },
    onMove: (sample) => {
      // Dragging over text would select it underneath the gesture.
      globalThis.getSelection()?.removeAllRanges();
      setDragPx(panelOffsetFor(sample.dx));
    },
    onEnd: (sample) => {
      const { committed, direction } = computeCommit(sample, { commitPx: COMMIT_THRESHOLD_PX });
      // Release the drag BEFORE closing: this way the panel goes from where
      // the finger left it to off-screen with the transition, instead of jumping.
      setIsDragging(false);
      setDragPx(0);
      if (committed && direction === "left") {
        options.close();
      }
    },
    onCancel: () => {
      setIsDragging(false);
      setDragPx(0);
    },
  });

  const backdropOpacity = () => {
    if (!isDragging()) {
      return 1;
    }
    const travelled = Math.abs(Math.min(dragPx(), 0));
    return Math.max(0, 1 - travelled / widthPx);
  };

  const owner = getOwner();
  let detach: (() => void) | undefined;
  if (owner) {
    runWithOwner(owner, () => {
      onCleanup(() => {
        detach?.();
        detach = undefined;
      });
    });
  }

  return {
    dragPx,
    isDragging,
    backdropOpacity,
    attach: (element) => {
      detach?.();
      detach = attachTouchMouse(element, recognizer);
    },
  };
}
